import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import {
  listFbFields, createFbField, updateFbField, deleteFbField,
  listFbAccounts, createFbAccount, updateFbAccount, deleteFbAccount, revealFbAccount,
} from '@/api/facebook';
import { isRevealPending } from '@/api/reveal';
import type { FbFieldDef, FbFieldType, FbAccountListItem, FbAccountSecret } from '@/api/types';

const FIELD_TYPES: FbFieldType[] = ['text', 'textarea', 'url', 'number'];

type AccountModal = 'create' | 'edit' | 'delete' | null;

export function Facebook() {
  const qc = useQueryClient();
  const { data: fields = [] } = useQuery({ queryKey: ['fb-fields'], queryFn: listFbFields });
  const { data: accounts = [], isLoading } = useQuery({ queryKey: ['fb-accounts'], queryFn: listFbAccounts });

  const [manageOpen, setManageOpen] = useState(false);
  const [accModal, setAccModal] = useState<AccountModal>(null);
  const [active, setActive] = useState<FbAccountListItem | null>(null);
  const [name, setName] = useState('');
  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [revealed, setRevealed] = useState<Record<string, FbAccountSecret>>({});
  const [revealing, setRevealing] = useState<string | null>(null);
  // Accounts whose reveal is waiting for a super admin. There is no polling:
  // the next click on the same button is the check.
  const [awaitingApproval, setAwaitingApproval] = useState<Record<string, true>>({});

  const invAccounts = () => qc.invalidateQueries({ queryKey: ['fb-accounts'] });

  const createAcc = useMutation({
    mutationFn: createFbAccount,
    onSuccess: () => { invAccounts(); setAccModal(null); },
    onError: (e: Error) => setError(e.message),
  });
  const updateAcc = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; values: Record<string, string> } }) => updateFbAccount(id, data),
    onSuccess: (_d, v) => {
      invAccounts(); setAccModal(null);
      setRevealed((p) => { const n = { ...p }; delete n[v.id]; return n; });
    },
    onError: (e: Error) => setError(e.message),
  });
  const deleteAcc = useMutation({
    mutationFn: (id: string) => deleteFbAccount(id),
    onSuccess: () => { invAccounts(); setAccModal(null); },
    onError: (e: Error) => setError(e.message),
  });

  const openCreate = () => { setName(''); setForm({}); setError(''); setActive(null); setAccModal('create'); };
  const openEdit = (a: FbAccountListItem) => {
    setActive(a); setName(a.name); setError('');
    // Seed only non-secret values; secret inputs start blank ("leave to keep").
    setForm({ ...a.values });
    setAccModal('edit');
  };
  const openDelete = (a: FbAccountListItem) => { setActive(a); setError(''); setAccModal('delete'); };

  const submitAcc = () => {
    if (!name.trim()) { setError('Name is required'); return; }
    // Send only non-empty fields; blank secret inputs are omitted so they stay unchanged.
    const values: Record<string, string> = {};
    for (const f of fields) {
      const v = (form[f.key] ?? '').trim();
      if (v) values[f.key] = v;
    }
    if (accModal === 'create') createAcc.mutate({ name: name.trim(), values });
    else if (accModal === 'edit' && active) updateAcc.mutate({ id: active.id, data: { name: name.trim(), values } });
  };

  const toggleReveal = async (a: FbAccountListItem) => {
    if (revealed[a.id]) { setRevealed((p) => { const n = { ...p }; delete n[a.id]; return n; }); return; }
    setRevealing(a.id);
    try {
      const res = await revealFbAccount(a.id);
      if (isRevealPending(res)) {
        // The toast in the API client already explains what happened; the row
        // only has to stop looking like a failed click.
        setAwaitingApproval((p) => ({ ...p, [a.id]: true }));
        return;
      }
      setAwaitingApproval((p) => { const n = { ...p }; delete n[a.id]; return n; });
      setRevealed((p) => ({ ...p, [a.id]: res }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reveal');
    } finally { setRevealing(null); }
  };

  const secretFields = fields.filter((f) => f.is_secret);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-content">Facebook</h1>
        <EditOnly>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setManageOpen(true)}>Manage fields</Button>
            <Button onClick={openCreate} disabled={fields.length === 0}>Add account</Button>
          </div>
        </EditOnly>
      </div>

      {accModal === null && error && <p className="text-danger text-sm mb-3">{error}</p>}
      {fields.length === 0 && (
        <p className="text-content-muted text-sm mb-3">No fields defined yet. Click “Manage fields” to add some.</p>
      )}

      {isLoading ? (
        <p className="text-content-muted text-sm">Loading…</p>
      ) : accounts.length === 0 ? (
        <p className="text-content-muted text-sm">No accounts yet.</p>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-content-muted">
              <tr>
                <th className="text-left font-medium px-3 py-2 whitespace-nowrap">Name</th>
                {fields.map((f) => (
                  <th key={f.id} className="text-left font-medium px-3 py-2 whitespace-nowrap">{f.label}</th>
                ))}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const sec = revealed[a.id];
                return (
                  <tr key={a.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-medium text-content whitespace-nowrap">{a.name}</td>
                    {fields.map((f) => (
                      <td key={f.id} className="px-3 py-2 font-mono">
                        {f.is_secret ? (
                          sec ? (
                            <span className="text-content">
                              {sec.values[f.key] || '—'}
                              {sec.values[f.key] && (
                                <button
                                  onClick={() => navigator.clipboard.writeText(sec.values[f.key]!)}
                                  className="ml-2 text-xs text-primary hover:underline"
                                  title={`Copy ${f.label}`}
                                >copy</button>
                              )}
                            </span>
                          ) : awaitingApproval[a.id] ? (
                            <span className="text-warning text-xs font-sans" title="A super admin has to approve the request in Telegram">
                              ⏳ waiting
                            </span>
                          ) : (
                            <span className="text-content-subtle">{a.has[f.key] ? '••••••' : '—'}</span>
                          )
                        ) : f.type === 'url' && a.values[f.key] ? (
                          <a href={a.values[f.key]} target="_blank" rel="noreferrer" className="text-primary hover:underline">{a.values[f.key]}</a>
                        ) : (
                          <span className="text-content-muted">{a.values[f.key] || '—'}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {secretFields.some((f) => a.has[f.key]) && (
                        <>
                          {sec?.grant_expires_at != null && (
                            <span className="text-content-subtle text-xs mr-2">
                              until {new Date(sec.grant_expires_at * 1000).toISOString().slice(11, 16)} UTC
                            </span>
                          )}
                          <button
                            onClick={() => toggleReveal(a)}
                            disabled={revealing === a.id}
                            className="text-primary hover:underline disabled:opacity-50 mr-3"
                          >
                            {sec ? 'Hide'
                              : revealing === a.id ? '…'
                                : awaitingApproval[a.id] ? 'Check approval'
                                  : 'Show'}
                          </button>
                        </>
                      )}
                      <EditOnly>
                        <button onClick={() => openEdit(a)} className="text-content-muted hover:text-content mr-3">Edit</button>
                        <button onClick={() => openDelete(a)} className="text-danger hover:underline">Delete</button>
                      </EditOnly>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(accModal === 'create' || accModal === 'edit') && (
        <Modal isOpen onClose={() => setAccModal(null)} title={accModal === 'create' ? 'New account' : 'Edit account'}>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Name *</label>
              <input className="w-full px-2 py-1.5 rounded-md border border-border bg-surface text-sm"
                value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {fields.map((f) => (
              <div key={f.id}>
                <label className="block text-xs font-medium text-content-muted mb-1">
                  {f.label}
                  {accModal === 'edit' && f.is_secret && (
                    <span className="text-content-subtle"> (leave blank to keep unchanged)</span>
                  )}
                </label>
                {f.type === 'textarea' ? (
                  <textarea className="w-full px-2 py-1.5 rounded-md border border-border bg-surface text-sm" rows={2}
                    value={form[f.key] ?? ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))} />
                ) : (
                  <input type={f.type === 'number' ? 'number' : 'text'}
                    className="w-full px-2 py-1.5 rounded-md border border-border bg-surface text-sm"
                    value={form[f.key] ?? ''} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))} />
                )}
              </div>
            ))}
            {error && <p className="text-danger text-sm">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setAccModal(null)}>Cancel</Button>
              <Button onClick={submitAcc} disabled={createAcc.isPending || updateAcc.isPending}>Save</Button>
            </div>
          </div>
        </Modal>
      )}

      {accModal === 'delete' && active && (
        <Modal isOpen onClose={() => setAccModal(null)} title="Delete account">
          <p className="text-sm text-content-muted mb-4">Delete “{active.name}”? This cannot be undone.</p>
          {error && <p className="text-danger text-sm mb-2">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAccModal(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => deleteAcc.mutate(active.id)} disabled={deleteAcc.isPending}>Delete</Button>
          </div>
        </Modal>
      )}

      {manageOpen && (
        <ManageFieldsModal fields={fields} onClose={() => setManageOpen(false)}
          onChanged={() => { qc.invalidateQueries({ queryKey: ['fb-fields'] }); invAccounts(); }} />
      )}
    </div>
  );
}

function ManageFieldsModal({ fields, onClose, onChanged }: {
  fields: FbFieldDef[]; onClose: () => void; onChanged: () => void;
}) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<FbFieldType>('text');
  const [isSecret, setIsSecret] = useState(false);
  const [err, setErr] = useState('');

  const add = useMutation({
    mutationFn: () => createFbField({ label: label.trim(), type, is_secret: isSecret, position: fields.length }),
    onSuccess: () => { setLabel(''); setType('text'); setIsSecret(false); onChanged(); },
    onError: (e: Error) => setErr(e.message),
  });
  const toggleSecret = useMutation({
    mutationFn: (f: FbFieldDef) => updateFbField(f.id, { is_secret: !f.is_secret }),
    onSuccess: onChanged, onError: (e: Error) => setErr(e.message),
  });
  const rename = useMutation({
    mutationFn: ({ id, label: l }: { id: string; label: string }) => updateFbField(id, { label: l }),
    onSuccess: onChanged, onError: (e: Error) => setErr(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteFbField(id),
    onSuccess: onChanged, onError: (e: Error) => setErr(e.message),
  });

  return (
    <Modal isOpen onClose={onClose} title="Manage fields">
      <div className="space-y-3">
        {err && <p className="text-danger text-sm">{err}</p>}
        <div className="border border-border rounded-lg divide-y divide-border">
          {fields.length === 0 && <p className="text-content-muted text-sm p-3">No fields yet.</p>}
          {fields.map((f) => (
            <div key={f.id} className="flex items-center gap-2 p-2">
              <input
                className="flex-1 px-2 py-1 rounded-md border border-border bg-surface text-sm"
                defaultValue={f.label}
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== f.label) rename.mutate({ id: f.id, label: v }); }}
              />
              <span className="text-xs text-content-subtle w-16 shrink-0">{f.type}</span>
              <label className="text-xs text-content-muted flex items-center gap-1 shrink-0">
                <input type="checkbox" checked={f.is_secret} onChange={() => toggleSecret.mutate(f)} /> secret
              </label>
              <button onClick={() => remove.mutate(f.id)} className="text-danger hover:underline text-xs shrink-0">del</button>
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-3">
          <p className="text-xs font-medium text-content-muted mb-2">Add field</p>
          <div className="flex flex-wrap items-center gap-2">
            <input className="flex-1 min-w-[8rem] px-2 py-1.5 rounded-md border border-border bg-surface text-sm"
              placeholder="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
            <select className="px-2 py-1.5 rounded-md border border-border bg-surface text-sm"
              value={type} onChange={(e) => setType(e.target.value as FbFieldType)}>
              {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="text-xs text-content-muted flex items-center gap-1">
              <input type="checkbox" checked={isSecret} onChange={(e) => setIsSecret(e.target.checked)} /> secret
            </label>
            <Button onClick={() => { setErr(''); if (label.trim()) add.mutate(); }} disabled={add.isPending}>Add</Button>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

export default Facebook;
